import { getSupabaseClient } from '../../lib/supabaseClient';
import { getPocketDB } from '../pocketDb';
import { normalizeModules } from '../../auth/moduleAccess';

/** Pull committees for an org into RxDB. Prefer loadCommitteesCloud for full data. */
export async function pullCommitteesIntoRxDB(organizationId) {
  const supabase = getSupabaseClient();
  if (!supabase || !organizationId) return 0;

  const { data, error } = await supabase
    .from('committees')
    .select('id, organization_id, name, chair_id, chair_user_id, is_standing, description, updated_at')
    .eq('organization_id', organizationId);

  if (error) {
    // Older schemas may lack chair_user_id / description — fall back
    const fallback = await supabase
      .from('committees')
      .select('id, organization_id, name, chair_id, is_standing, updated_at')
      .eq('organization_id', organizationId);
    if (fallback.error) {
      console.warn('[membersSync] committees pull failed', error.message);
      return 0;
    }
    return upsertCommitteeRowsLocal(fallback.data || [], false);
  }
  return upsertCommitteeRowsLocal(data || [], true);
}

async function upsertCommitteeRowsLocal(data, hasExtras) {
  if (!data?.length) return 0;
  const db = await getPocketDB();
  await Promise.all(
    data.map((row) =>
      db.committees.upsert({
        id: row.id,
        organization_id: row.organization_id,
        name: row.name,
        chair_id: row.chair_user_id || row.chair_id || '',
        is_standing: row.is_standing ?? true,
        description: hasExtras ? row.description || '' : '',
        chair_name: '',
        members: [],
        referred_items: [],
        updated_at: row.updated_at ?? new Date().toISOString(),
      })
    )
  );
  return data.length;
}

/** Push a committee create/update to Supabase when online. */
export async function upsertCommitteeRemote(organizationId, committee) {
  // Prefer full saveCommitteeCloud when memberships are present
  try {
    const { saveCommitteeCloud } = await import('./committeesSync');
    if (Array.isArray(committee.memberships) || committee.chairUserId) {
      return saveCommitteeCloud(organizationId, committee);
    }
  } catch {
    /* fall through */
  }

  const supabase = getSupabaseClient();
  if (!supabase || !organizationId) return committee;

  const payload = {
    organization_id: organizationId,
    name: committee.name,
    description: committee.description || '',
    is_standing: committee.type !== 'special' && committee.is_standing !== false,
    chair_user_id: committee.chairUserId || null,
    updated_at: new Date().toISOString(),
  };

  const looksLikeUuid =
    typeof committee.id === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      committee.id
    );

  if (looksLikeUuid) {
    const { data, error } = await supabase
      .from('committees')
      .update(payload)
      .eq('id', committee.id)
      .eq('organization_id', organizationId)
      .select('id, organization_id, name, chair_id, chair_user_id, is_standing, description, updated_at')
      .maybeSingle();
    if (!error && data) {
      const db = await getPocketDB();
      await db.committees.upsert({
        id: data.id,
        organization_id: data.organization_id,
        name: data.name,
        chair_id: data.chair_user_id || data.chair_id || '',
        is_standing: data.is_standing,
        description: data.description ?? committee.description ?? '',
        chair_name: committee.chair ?? '',
        members: committee.members ?? [],
        referred_items: committee.referredItems ?? [],
        updated_at: data.updated_at,
      });
      return { ...committee, id: data.id };
    }
  }

  const { data: created, error: createErr } = await supabase
    .from('committees')
    .insert(payload)
    .select('id, organization_id, name, chair_id, chair_user_id, is_standing, description, updated_at')
    .single();

  if (createErr || !created) {
    console.warn('[membersSync] committee upsert failed', createErr?.message);
    return committee;
  }

  const db = await getPocketDB();
  await db.committees.upsert({
    id: created.id,
    organization_id: created.organization_id,
    name: created.name,
    chair_id: created.chair_user_id || created.chair_id || '',
    is_standing: created.is_standing,
    description: created.description ?? committee.description ?? '',
    chair_name: committee.chair ?? '',
    members: committee.members ?? [],
    referred_items: committee.referredItems ?? [],
    updated_at: created.updated_at,
  });

  return {
    ...committee,
    id: created.id,
    type: created.is_standing ? 'standing' : 'special',
  };
}

/** Upsert organization row locally (from signup or pull). */
export async function upsertOrganizationLocal(org) {
  const db = await getPocketDB();
  await db.organizations.upsert({
    id: org.id,
    name: org.name,
    bylaws_config: org.bylaws_config ?? {},
    quorum_threshold: org.quorum_threshold ?? 50,
    updated_at: new Date().toISOString(),
  });
}

/** User shell profile (no org/role). */
export async function upsertProfileLocal(profile) {
  const db = await getPocketDB();
  await db.profiles.upsert({
    id: profile.id,
    display_name: profile.display_name ?? '',
    updated_at: new Date().toISOString(),
  });
}

export async function upsertMembershipLocal(membership) {
  const db = await getPocketDB();
  await db.organization_memberships.upsert({
    id: membership.id,
    user_id: membership.user_id,
    organization_id: membership.organization_id,
    role: membership.role,
    modules: normalizeModules(membership.role, membership.modules),
    is_active: membership.is_active !== false,
    organization_name: membership.organization_name ?? '',
    updated_at: new Date().toISOString(),
  });
}

export async function upsertMeetingLocal(meeting) {
  const db = await getPocketDB();
  await db.meetings.upsert({
    id: meeting.id,
    organization_id: meeting.organization_id,
    date: meeting.date ?? new Date().toISOString(),
    minutes_raw: meeting.minutes_raw ?? '',
    is_closed: Boolean(meeting.is_closed),
    updated_at: new Date().toISOString(),
  });
}

/** Create or return an open meeting for the org in Supabase + local DB. */
export async function ensureOrgMeeting(organizationId) {
  const supabase = getSupabaseClient();
  if (!supabase || !organizationId) return null;

  try {
    const { data: existing, error: findErr } = await supabase
      .from('meetings')
      .select('id, organization_id, date, minutes_raw, is_closed, updated_at')
      .eq('organization_id', organizationId)
      .eq('is_closed', false)
      .order('date', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (findErr) {
      console.warn('[membersSync] find meeting failed', findErr.message);
    } else if (existing) {
      try {
        await upsertMeetingLocal(existing);
      } catch (err) {
        console.warn('[membersSync] local meeting upsert failed', err?.message || err);
      }
      return existing;
    }

    const { data: created, error } = await supabase
      .from('meetings')
      .insert({
        organization_id: organizationId,
        date: new Date().toISOString(),
        is_closed: false,
      })
      .select('id, organization_id, date, minutes_raw, is_closed, updated_at')
      .single();

    if (error) {
      console.warn('[membersSync] create meeting failed', error.message);
      return null;
    }

    try {
      await upsertMeetingLocal(created);
    } catch (err) {
      console.warn('[membersSync] local meeting upsert failed', err?.message || err);
    }
    return created;
  } catch (err) {
    console.warn('[membersSync] ensureOrgMeeting failed', err?.message || err);
    return null;
  }
}

export async function loadCommitteesFromLocal(organizationId) {
  if (!organizationId) return [];
  const db = await getPocketDB();
  const docs = await db.committees
    .find({ selector: { organization_id: organizationId } })
    .exec();
  return docs.map((d) => {
    const row = d.toJSON();
    return {
      id: row.id,
      name: row.name,
      type: row.is_standing ? 'standing' : 'special',
      chair: row.chair_name || '',
      members: Array.isArray(row.members) ? row.members : [],
      description: row.description || '',
      referredItems: Array.isArray(row.referred_items) ? row.referred_items : [],
      created: row.updated_at ? new Date(row.updated_at).toLocaleDateString() : '',
    };
  });
}
